/** One reconciliation, ranked. SPEC-0002. */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { RunDetail as RunDetailType } from '@picklii/shared';
import { AppShell } from '../components/AppShell';
import { FindingsTable, UnmatchedRoles, UnscoredFindings } from '../components/Findings';
import { ConfirmAction } from '../components/ConfirmAction';
import { RowExclusions } from '../components/RowExclusions';
import { RunMappingPanel } from '../components/RunMappingPanel';
import { RunSummaryStats } from '../components/RunSummaryStats';
import { api } from '../lib/api';
import { shortDate } from '../lib/format';

export function RunDetail() {
	const { id = '' } = useParams();
	const navigate = useNavigate();
	const [run, setRun] = useState<RunDetailType | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [deleting, setDeleting] = useState(false);

	async function remove() {
		setDeleting(true);
		setError(null);
		try {
			await api.deleteRun(id);
			navigate('/runs');
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Could not delete this reconciliation.');
			setDeleting(false);
		}
	}

	useEffect(() => {
		api.getRun(id).then(setRun, (cause: Error) => setError(cause.message));
	}, [id]);

	return (
		<AppShell
			title="Reconciliation"
			breadcrumbs={
				<div className="breadcrumbs text-sm">
					<ul>
						<li>
							<Link to="/runs">Reconciliations</Link>
						</li>
						<li>
							<h1>{run === null ? 'Loading…' : shortDate(run.createdAt)}</h1>
						</li>
					</ul>
				</div>
			}>
			{error !== null && (
				<div className="alert alert-error alert-soft col-span-12" role="alert">
					<span>{error}</span>
				</div>
			)}

			{run === null && error === null && (
				<div className="col-span-12 flex justify-center py-16">
					<span className="loading loading-spinner" />
				</div>
			)}

			{run !== null && (
				<>
					<RunSummaryStats summary={run.summary} />

					{/*
					 * Before the findings, not after: a viewer needs to know what the
					 * numbers below do not include before trusting them. SPEC-0001 #2.
					 */}
					<RowExclusions rowErrors={run.rowErrors} />

					<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
						<div className="card-body gap-4">
							<div>
								<h2 className="card-title text-base">Findings, most urgent first</h2>
								<p className="text-base-content/70 text-sm">
									Ranked by dollar exposure weighted by how much of the (possibly wrong) budget
									is already spent, and by direction — a budget that is too high is leaking
									margin right now. Score is severity, not dollars at risk.
								</p>
							</div>
							<FindingsTable findings={run.findings} />
						</div>
					</section>

					<UnscoredFindings findings={run.unscoredFindings} />
					<UnmatchedRoles items={run.unmatchedRoles} />
					{run.mapping !== undefined && <RunMappingPanel mapping={run.mapping} />}

					{/*
					 * At the foot of the page, below the findings, so the record is in
					 * view before it can be destroyed. Deleting removes the run and
					 * every finding under it, and there is no undo.
					 */}
					<section className="col-span-12 flex flex-wrap items-center gap-3">
						<ConfirmAction
							label="Delete reconciliation"
							confirmLabel={`Delete the ${shortDate(run.createdAt)} reconciliation?`}
							description={`This removes the run and all ${
								run.findings.length + run.unscoredFindings.length
							} of its findings.`}
							busy={deleting}
							onConfirm={() => void remove()}
						/>
					</section>
				</>
			)}
		</AppShell>
	);
}
